import { transition } from '@helpers/animate';
import { imageCoverContainer } from '@helpers/aspectRatio';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { baseGrid } from '@helpers/grid';
import { Device, from, until } from '@helpers/media';
import { ComponentTheme, ComponentThemeTint } from '@helpers/themeEnum';
import { contentStyles, h1 } from '@helpers/typography';
import svgTick from '@img/icons/tick-rounded.svg?url';
import { ButtonStyles } from '@stories/Components/Buttons/GeneralButton/Button.styles';
import SectionWrapper from '@stories/Components/Global/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Global/SectionWrapper/SectionWrapper.styles';
import FieldStyles from '@stories/Components/ZohoForms/Components/Field/Field.styles';
import FieldRowStyles from '@stories/Components/ZohoForms/Components/FieldRow/FieldRow.styles';
import styled from 'styled-components';
const Container = styled(SectionWrapper) `
  --bulletColour: ${brand.orange.base};
  --textColour: --fgColor;
  --bgColour: transparent;
  --formAccentColour: ${brand.orange.base};
  --formBgColour: ${brand.orange.light};
  --formButtonColour: ${brand.black};
  --formButtonTextColour: ${brand.white};
  --formButtonHoverColour: ${brand.orange.base};
  --formButtonTextHoverColour: ${brand.black};
  --formFgColour: ${brand.black};

  position: relative;
  overflow: hidden;
  overflow: clip;
  background-color: var(--bgColour);

  &[data-theme=${ComponentTheme.Blue}] {
    --bgColour: ${brand.blue.base};

    &[data-tint='${ComponentThemeTint.TenPercent}'] {
      --bgColour: ${brand.blue.tint};
    }

    &[data-tint='${ComponentThemeTint.ThirtyPercent}'] {
      --bgColour: ${brand.blue.light};

      ${ButtonStyles.ButtonInner} {
        --hoverColour: ${brand.blue.tint};
      }
    }

    &:not([data-tint]) {
      ${ButtonStyles.ButtonInner} {
        --brandColour: ${brand.blue.tint};
      }
    }
  }

  &[data-theme=${ComponentTheme.Green}] {
    --bgColour: ${brand.green.base};

    &[data-tint='${ComponentThemeTint.TenPercent}'] {
      --bgColour: ${brand.green.tint};
    }

    &[data-tint='${ComponentThemeTint.ThirtyPercent}'] {
      --bgColour: ${brand.green.light};

      ${ButtonStyles.ButtonInner} {
        --hoverColour: ${brand.green.tint};
      }
    }

    &:not([data-tint]) {
      ${ButtonStyles.ButtonInner} {
        --brandColour: ${brand.green.tint};
      }
    }
  }

  &[data-theme=${ComponentTheme.Orange}] {
    --bgColour: ${brand.orange.base};
    --bulletColour: ${brand.grey.grey16};

    &[data-tint='${ComponentThemeTint.TenPercent}'] {
      --bgColour: ${brand.orange.tint};
    }

    &[data-tint='${ComponentThemeTint.ThirtyPercent}'] {
      --bgColour: ${brand.orange.light};
      --formBgColour: ${brand.orange.tint};

      ${ButtonStyles.ButtonInner} {
        --hoverColour: ${brand.orange.tint};
      }
    }

    &:not([data-tint]) {
      ${ButtonStyles.ButtonInner} {
        --brandColour: ${brand.orange.tint};
      }
    }
  }

  &[data-theme=${ComponentTheme.Yellow}] {
    --bgColour: ${brand.yellow.base};

    &[data-tint='${ComponentThemeTint.TenPercent}'] {
      --bgColour: ${brand.yellow.tint};
    }

    &[data-tint='${ComponentThemeTint.ThirtyPercent}'] {
      --bgColour: ${brand.yellow.light};

      ${ButtonStyles.ButtonInner} {
        --hoverColour: ${brand.yellow.tint};
      }
    }

    &:not([data-tint]) {
      ${ButtonStyles.ButtonInner} {
        --brandColour: ${brand.yellow.tint};
      }
    }
  }

  &[data-theme=${ComponentTheme.Black}] {
    --bgColour: ${brand.grey.grey16};
  }

  &[data-white-text='true'],
  &[data-theme=${ComponentTheme.Black}] {
    --textColour: ${brand.white};
  }

  &[data-form-theme=${ComponentTheme.Blue}] {
    --formBgColour: ${brand.blue.base};

    &[data-form-tint='${ComponentThemeTint.TenPercent}'] {
      --formBgColour: ${brand.blue.tint};
    }

    &[data-form-tint='${ComponentThemeTint.ThirtyPercent}'] {
      --formBgColour: ${brand.blue.light};

      ${ButtonStyles.ButtonInner} {
        --hoverColour: ${brand.blue.tint};
      }
    }

    &[data-form-tint] {
      --formAccentColour: ${brand.blue.base};
      --formButtonHoverColour: ${brand.blue.base};
      --formButtonTextHoverColour: ${brand.black};
    }

    &:not([data-form-tint]) {
      --formAccentColour: ${brand.black};
      --formButtonHoverColour: ${brand.blue.light};
      --formButtonTextHoverColour: ${brand.black};
    }
  }

  &[data-form-theme=${ComponentTheme.Green}] {
    --formBgColour: ${brand.green.base};

    &[data-form-tint='${ComponentThemeTint.TenPercent}'] {
      --formBgColour: ${brand.green.tint};
    }

    &[data-form-tint='${ComponentThemeTint.ThirtyPercent}'] {
      --formBgColour: ${brand.green.light};
    }

    &[data-form-tint] {
      --formAccentColour: ${brand.green.base};
      --formButtonHoverColour: ${brand.green.base};
      --formButtonTextHoverColour: ${brand.white};
    }

    &:not([data-form-tint]) {
      --formAccentColour: ${brand.black};
      --formButtonHoverColour: ${brand.green.light};
      --formButtonTextHoverColour: ${brand.black};
    }
  }

  &[data-form-theme=${ComponentTheme.Orange}] {
    --formBgColour: ${brand.orange.base};

    &[data-form-tint='${ComponentThemeTint.TenPercent}'] {
      --formBgColour: ${brand.orange.tint};
    }

    &[data-form-tint='${ComponentThemeTint.ThirtyPercent}'] {
      --formBgColour: ${brand.orange.light};
    }

    &:not([data-form-tint]) {
      --formAccentColour: ${brand.black};
      --formButtonHoverColour: ${brand.orange.light};
      --formButtonTextHoverColour: ${brand.black};
    }
  }

  &[data-form-theme=${ComponentTheme.Yellow}] {
    --formBgColour: ${brand.yellow.base};

    &[data-form-tint='${ComponentThemeTint.TenPercent}'] {
      --formBgColour: ${brand.yellow.tint};
    }

    &[data-form-tint='${ComponentThemeTint.ThirtyPercent}'] {
      --formBgColour: ${brand.yellow.light};
    }

    &[data-form-tint] {
      --formButtonHoverColour: ${brand.yellow.base};
      --formButtonTextHoverColour: ${brand.black};
    }

    &:not([data-form-tint]) {
      --formButtonHoverColour: ${brand.yellow.light};
      --formButtonTextHoverColour: ${brand.black};
    }
  }

  &[data-form-theme=${ComponentTheme.Black}] {
    --formAccentColour: ${brand.grey.grey44};
    --formBgColour: ${brand.grey.grey16};
    --formFgColour: ${brand.white};
    --formButtonColour: ${brand.white};
    --formButtonHoverColour: ${brand.grey.grey44};
    --formButtonTextColour: ${brand.black};
    --formButtonTextHoverColour: ${brand.white};
  }

  ${SectionWrapperStyles.SiteWide} {
    ${baseGrid};
  }
`;
const BackgroundImage = styled.img `
  ${imageCoverContainer()};

  &[data-hide-mobile='true'] {
    @media ${until(Device.Tablet)} {
      display: none;
    }
  }

  &[data-hide-desktop='true'] {
    @media ${from(Device.Tablet)} {
      display: none;
    }
  }
`;
const ContentWrapper = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  grid-column: 1 / span 12;
  margin: 0 0 ${fluid(38, 56)};
  color: var(--textColour);

  @media ${from(Device.TabletLarge)} {
    grid-column: 2 / span 10;
  }

  @media ${from(Device.Desktop)} {
    grid-column: 3 / span 8;
  }
`;
const Title = styled.h2 `
  ${h1}

  margin: 0;
`;
const Subtitle = styled.p `
  ${fonts.montserrat.bold};

  font-size: ${fluid(20, 26)};
  line-height: ${fluid(28, 36)};

  &:not(:first-child) {
    margin: ${fluid(14, 18)} 0 0;
  }
`;
const Content = styled.div `
  ${contentStyles};

  &:not(:first-child) {
    margin: ${fluid(24, 36)} 0 0;
  }

  ol,
  ul {
    text-align: left;

    @media ${from(Device.Tablet)} {
      width: 67%;
      margin-left: auto;
      margin-right: auto;
    }
  }

  ul {
    list-style-type: none;
    padding-left: 0;

    li {
      padding-left: ${fluid(28, 46)};
      position: relative;

      &::before,
      &::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 0;
        transform: translateY(-50%);
        height: ${fluid(20, 30)};
        width: ${fluid(20, 30)};
      }

      &::before {
        background-color: var(--bulletColour);
        border-radius: 9999px;
      }

      &::after {
        background-image: url('${svgTick}');
        background-position: center;
        background-repeat: no-repeat;
        background-size: ${fluid(10, 14)} auto;
      }
    }
  }
`;
const TrustpilotWrapper = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  grid-column: span 12;
  margin: 0 0 ${fluid(38, 56)};
  color: var(--textColour);

  @media ${from(Device.Tablet)} {
    grid-column: 2 / span 10;
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 3 / span 8;
  }

  @media ${from(Device.DesktopLarge)} {
    grid-column: 4 / span 6;
  }

  ${Content} {
    margin-top: 22px;
  }
`;
const TrustPilotLogo = styled.img `
  display: block;
  width: 300px;
`;
const TrustPilotHeading = styled.h3 `
  ${fonts.montserrat.bold};

  font-size: ${fluid(20, 26)};
  line-height: ${fluid(28, 36)};
  margin: ${fluid(32, 44)} 0 0;
`;
const FormWrapper = styled.div `
  position: relative;
  grid-column: span 12;
  padding: ${fluid(28, 48)} ${fluid(20, 74)} ${fluid(32, 48)};
  background-color: var(--formBgColour);
  border-radius: ${fluid(25, 50)};

  ${FieldRowStyles.Container} {
    --accentColour: var(--formAccentColour);

    display: grid;
    grid-template-columns: repeat(1, 1fr) !important;
    gap: 24px;
    margin: 0 0 24px;

    input,
    input::placeholder,
    label {
      font-size: 18px;
    }

    label {
      color: var(--formFgColour);
    }
  }

  ${FieldStyles.Field} {
    grid-column: 1 !important;
    margin: 0;
  }

  ${ButtonStyles.Button} {
    --brandColour: var(--formButtonColour);
    --hoverColour: var(--formButtonHoverColour);

    ${ButtonStyles.ButtonInner} {
      ${transition('color')};

      color: var(--formButtonTextColour);
    }

    &:hover,
    &:active {
      ${ButtonStyles.ButtonInner} {
        color: var(--formButtonTextHoverColour);
      }
    }

    @media ${until(Device.TabletLarge)} {
      width: 100%;
      text-align: center;
      justify-content: center;
      margin-top: 40px;
    }
  }

  @media ${from(Device.Tablet)} {
    grid-column: 2 / span 10;
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 3 / span 8;
  }

  @media ${from(Device.DesktopLarge)} {
    grid-column: 4 / span 6;
  }
`;
const FormTitle = styled.h3 `
  ${fonts.lustria.regular};

  font-size: ${fluid(24, 32)};
  line-height: ${fluid(40, 60)};
  margin: 0 0 ${fluid(26, 30)};
  color: var(--formFgColour);
`;
const OfferFormBlockStyles = {
    BackgroundImage,
    Container,
    Content,
    ContentWrapper,
    FormTitle,
    FormWrapper,
    Subtitle,
    Title,
    TrustPilotHeading,
    TrustPilotLogo,
    TrustpilotWrapper,
};
export default OfferFormBlockStyles;
